function normalizeSuggestKey(value: string): string {
  return value.trim().toLocaleLowerCase('en-US');
}

/** 목록에 있는 값이면 원본(대소문자 유지)을 반환, 없으면 null */
export function findCanonicalSuggestValue(
  value: string,
  options: string[],
): string | null {
  const key = normalizeSuggestKey(value);
  if (!key) return null;
  const found = options.find(option => normalizeSuggestKey(option) === key);
  return found ?? null;
}

export function isStrictSuggestValue(value: string, options: string[]): boolean {
  const val = value.trim();
  if (!val) return true;
  return findCanonicalSuggestValue(val, options) != null;
}

export function filterSuggestOptions(
  query: string,
  options: string[],
  limit = 10,
): { items: string[]; total: number } {
  const q = normalizeSuggestKey(query);
  const matched = !q
    ? options
    : options.filter(option => normalizeSuggestKey(option).includes(q));
  return {
    items: matched.slice(0, limit),
    total: matched.length,
  };
}

/** 자동완성 헤더 건수 — 더 있으면 "12 / 45건", 전부 표시면 "8건 표시" */
export function formatSuggestCountLabel(shown: number, total: number): string {
  if (total > shown) return `${shown} / ${total}건`;
  return `${shown}건 표시`;
}

export interface SuggestDropdownPosition {
  left: number;
  top: number;
  minWidth: number;
  maxHeight: number;
}

export function computeSuggestDropdownPosition(
  rect: DOMRect,
  itemCount: number,
  options?: { itemHeight?: number; maxVisible?: number; headerHeight?: number },
): SuggestDropdownPosition {
  const itemHeight = options?.itemHeight ?? 40;
  const maxVisible = options?.maxVisible ?? 8;
  const headerHeight = options?.headerHeight ?? 44;
  const visibleCount = Math.max(1, Math.min(itemCount, maxVisible));
  const dropdownHeight = visibleCount * itemHeight + headerHeight;
  const spaceBelow = window.innerHeight - rect.bottom;
  const spaceAbove = rect.top;
  let top = rect.bottom + window.scrollY;
  if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
    top = rect.top + window.scrollY - dropdownHeight;
  }
  return {
    left: rect.left + window.scrollX,
    top,
    minWidth: Math.max(rect.width, 260),
    maxHeight: maxVisible * itemHeight,
  };
}

export const CONTAINER_MASTER_SUGGEST_REJECT_MESSAGE =
  '컨테이너 마스터 관리에 먼저 등록 후에 입력하세요.';

export const COMPANY_SUGGEST_REJECT_MESSAGE =
  '거래처정보 관리에 먼저 등록 후에 입력하세요.';

export const CARRIER_SUGGEST_REJECT_MESSAGE =
  '거래처정보 관리(컨테이너 운송업체)에 먼저 등록 후에 입력하세요.';

/** 도착 창고·LINE·터미널 — 컨테이너 마스터 관리 strict 자동완성 열 */
export function isContainerMasterStrictSuggestField(
  row: number,
  col: number,
  rowsPerRecord: number,
): boolean {
  const isTopRow = row % rowsPerRecord === 0;
  return (col === 1 && isTopRow) || (col === 4 && isTopRow) || col === 9;
}

/** strict 자동완성 거부 시 표시할 안내 (해당 없으면 null) */
export function getStrictSuggestRejectMessage(
  row: number,
  col: number,
  rowsPerRecord: number,
): string | null {
  const isTopRow = row % rowsPerRecord === 0;
  if (col === 0 && isTopRow) return COMPANY_SUGGEST_REJECT_MESSAGE;
  if (col === 10 && isTopRow) return CARRIER_SUGGEST_REJECT_MESSAGE;
  if (isContainerMasterStrictSuggestField(row, col, rowsPerRecord)) {
    return CONTAINER_MASTER_SUGGEST_REJECT_MESSAGE;
  }
  return null;
}
